import React, { useState, useEffect } from 'react';
import { getCollection } from '../services/resilientDb';
import './DashboardPage.css';

const DashboardPage = () => {
    const [stats, setStats] = useState({
        totalWorkers: 0,
        totalJobRequests: 0,
        pendingRequests: 0,
        completedRequests: 0
    });
    const [recentJobs, setRecentJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            // Fetch workers (resilient: Firebase with Supabase fallback)
            const workers = await getCollection('serviceProviders');
            const approvedWorkers = workers.filter((worker) => {
                const status = String(worker?.status || '').toLowerCase();
                return worker?.isVerified === true || worker?.isActive === true || status === 'approved';
            });
            const totalWorkers = approvedWorkers.length;

            // Fetch job requests ordered by date (resilient)
            const jobs = await getCollection('bookings', {
                orderByField: 'createdAt',
                orderDirection: 'desc',
            });

            let pendingCount = 0;
            let completedCount = 0;
            jobs.forEach((job) => {
                if (job.status === 'pending') pendingCount++;
                if (job.status === 'completed') completedCount++;
            });

            setStats({
                totalWorkers,
                totalJobRequests: jobs.length,
                pendingRequests: pendingCount,
                completedRequests: completedCount,
            });

            setRecentJobs(jobs.slice(0, 5));
            setLoading(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="container">
                    <div className="loading">Loading dashboard...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <div className="container">
                <div className="dashboard-header">
                    <h1>Admin Dashboard</h1>
                    <p>Manage your service providers and job requests</p>
                </div>

                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon workers">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.totalWorkers}</h3>
                            <p>Total Workers</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon jobs">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                            </svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.totalJobRequests}</h3>
                            <p>Total Jobs</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon pending">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.pendingRequests}</h3>
                            <p>Pending Requests</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon completed">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <div className="stat-info">
                            <h3>{stats.completedRequests}</h3>
                            <p>Completed</p>
                        </div>
                    </div>
                </div>

                <div className="recent-section">
                    <div className="section-header">
                        <h2>Recent Job Requests</h2>
                        <a href="/job-requests" className="view-all-link">View All →</a>
                    </div>

                    {recentJobs.length === 0 ? (
                        <div className="empty-state">
                            <p>No job requests yet</p>
                        </div>
                    ) : (
                        <div className="jobs-list">
                            {recentJobs.map((job) => (
                                <div key={job.id} className="job-item">
                                    <div className="job-details">
                                        <h4>{job.serviceType || 'Service Request'}</h4>
                                        <p>{job.description || 'No description'}</p>
                                        <div className="job-meta">
                                            <span className="job-customer">{job.customerName || job.name}</span>
                                            <span className="job-date">
                                                {job.date || new Date(job.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={`job-status status-${job.status || 'pending'}`}>
                                        {job.status || 'pending'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
